import {FC} from 'react'
import {Content} from '@prismicio/client'
import {PrismicRichText, SliceComponentProps} from '@prismicio/react'
import {Container} from 'src/common/Container'
import {getButtonsData} from 'src/common/helpers'
import {Context} from 'src/common/types'
import {ButtonV2} from 'src/common/ButtonV2'
import {useLinkResolver} from 'prismicio'
import {TimelineCard} from './TimelineCard'

/**
 * Props for `TimelineSection`.
 */
export type TimelineSectionProps = SliceComponentProps<Content.TimelineSectionSlice> & {context: Context}

/**
 * Component for "TimelineSection" Slices.
 */
const TimelineSection: FC<TimelineSectionProps> = ({slice, context, index: componentIndex}) => {
  const {primaryButtonData} = getButtonsData({
    buttonsData: context.buttonsData,
    primaryButton: slice.primary.primary_button,
  })

  const {linkResolver} = useLinkResolver()

  return (
    <Container data-slice-type={slice.slice_type} data-slice-variation={slice.variation} className="py-10 md:!py-20">
      <div className="mx-auto flex w-full max-w-[1200px] flex-col gap-y-12 px-5 md:px-10 lg:px-5 xl:px-0">
        <div className="flex flex-col gap-6 pb-4">
          <PrismicRichText
            field={slice.primary.tag}
            components={{
              paragraph: ({children}) => (
                <p className="text-button-s font-semibold uppercase text-moss-grey-400">{children}</p>
              ),
              strong: ({children}) => <strong className="pr-3 font-semibold text-moss-blue">{children}</strong>,
            }}
          />
          <PrismicRichText
            field={slice.primary.heading}
            components={{
              paragraph: ({children}) => (
                <h2 className="md:text-tg-h2 col-span-full text-[32px] font-normal leading-9 tracking-[.32px] text-moss-grey-400 md:leading-[normal]">
                  {children}
                </h2>
              ),
              strong: ({children}) => (
                <>
                  <span className="md:text-tg-h2 text-[32px] font-normal leading-9 tracking-[.32px] text-black md:leading-[normal]">
                    {children}
                  </span>
                  <br className="max-lg:hidden" />
                </>
              ),
            }}
          />
        </div>
        <div className="grid gap-y-8 max-md:grid-rows-3 md:grid-cols-3 md:gap-x-6 md:gap-y-12">
          {slice.primary.sections.map((section, index) => (
            <TimelineCard key={section.time_label! + index} section={section} index={index} />
          ))}
        </div>
        <div className="col-span-full flex justify-center max-md:hidden">
          <ButtonV2
            variant="primary"
            trackingData={{
              mktgId: primaryButtonData?.marketing_id || '',
              pageName: context.pageUid,
              location: `component-${componentIndex}-timeline-section`,
              dataType: 'primary-button',
            }}
            href={linkResolver(primaryButtonData?.link, 'primary-button-timeline-section')}
          >
            {primaryButtonData?.label}
          </ButtonV2>
        </div>
      </div>
    </Container>
  )
}

export default TimelineSection
